import type { Field, JsonValue, OptionSchema, OptionValues } from "../api/types";

/**
 * Reading and writing a bot's answers.
 *
 * A port of the accessors on `OptionValues` in shared/.../api/Options.kt. The map is nested under a
 * `values` key on the wire, which is the sort of thing that is invisible until a form saves nothing
 * and nobody can say why.
 */

function map(options: OptionValues): Record<string, JsonValue> {
  return options.values ?? {};
}

export function readBoolean(options: OptionValues, key: string, fallback = false): boolean {
  const value = map(options)[key];
  return typeof value === "boolean" ? value : fallback;
}

export function readString(options: OptionValues, key: string, fallback = ""): string {
  const value = map(options)[key];
  return typeof value === "string" ? value : fallback;
}

export function readNumber(options: OptionValues, key: string): number | null {
  const value = map(options)[key];
  return typeof value === "number" ? value : null;
}

export function readList(options: OptionValues, key: string): string[] {
  const value = map(options)[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

export function withValue(options: OptionValues, key: string, value: JsonValue): OptionValues {
  return { values: { ...map(options), [key]: value } };
}

/** What a field holds before anybody has answered it. */
export function defaults(schema: OptionSchema): OptionValues {
  const values: Record<string, JsonValue> = {};
  for (const field of schema.fields ?? []) {
    values[field.key] = defaultValue(field);
  }
  return { values };
}

function defaultValue(field: Field): JsonValue {
  switch (field.type) {
    case "switch":
      return field.default ?? false;
    case "text":
      return field.default ?? "";
    case "number":
      return field.default ?? null;
    case "pick":
      return field.default;
    case "multipick":
      return field.default ?? [];
  }
}

/** What the bot called this field's kind, shown beside the label the way the design does. */
export function kindName(field: Field): string {
  switch (field.type) {
    case "switch":
      return "Switch";
    case "text":
      return "Text";
    case "number":
      return "Number, optional";
    case "pick":
      return "Pick";
    case "multipick":
      return (field.choices ?? []).length === 0 ? "MultiPick, bot-supplied" : "MultiPick";
  }
}

/** The stored payload, laid out so it can be read. */
export function pretty(options: OptionValues): string {
  return Object.entries(map(options))
    .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
    .join("\n");
}
